import { cn } from '../../utils/cn';

export type LifecycleStageId =
  | 'observation'
  | 'evidence'
  | 'rubric'
  | 'conference';

const STAGES: { id: LifecycleStageId; label: string }[] = [
  { id: 'observation', label: 'Observation' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'rubric', label: 'Rubric' },
  { id: 'conference', label: 'Conference' },
];

const STAGE_COLORS: Record<LifecycleStageId, string> = {
  observation: 'var(--se-route-red)',
  evidence: 'var(--se-route-amber)',
  rubric: 'var(--se-route-teal)',
  conference: 'var(--se-midnight-muted)',
};

interface EvaluationLifecycleRouteProps {
  activeStage?: LifecycleStageId;
  className?: string;
}

export function EvaluationLifecycleRoute({
  activeStage = 'observation',
  className,
}: EvaluationLifecycleRouteProps) {
  const activeIndex = STAGES.findIndex(s => s.id === activeStage);

  return (
    <section
      className={cn(
        'rounded-md border border-[var(--se-rule)] bg-[var(--se-porcelain-panel)] px-4 py-4 sm:px-5',
        className
      )}
      aria-labelledby="evaluation-lifecycle-heading"
    >
      <h2
        id="evaluation-lifecycle-heading"
        className="text-sm font-semibold text-[var(--se-ink)] tracking-tight"
      >
        Evaluation lifecycle
      </h2>
      <div
        className="mt-4 overflow-x-auto pb-1"
        role="list"
        aria-label="Evaluation stages"
      >
        <ol className="flex min-w-[32rem] items-start gap-0">
          {STAGES.map((stage, index) => {
            const isComplete = index < activeIndex;
            const isActive = index === activeIndex;
            const isUpcoming = index > activeIndex;
            const lineColor = isComplete
              ? STAGE_COLORS[stage.id]
              : 'var(--se-rule)';

            return (
              <li
                key={stage.id}
                className="relative flex flex-1 flex-col items-center text-center"
              >
                {index > 0 && (
                  <span
                    className="absolute left-0 top-[11px] h-0.5 w-1/2 -translate-x-1/2"
                    style={{ backgroundColor: lineColor }}
                    aria-hidden
                  />
                )}
                {index < STAGES.length - 1 && (
                  <span
                    className="absolute right-0 top-[11px] h-0.5 w-1/2 translate-x-1/2"
                    style={{
                      backgroundColor:
                        index < activeIndex
                          ? STAGE_COLORS[
                              STAGES[index + 1]?.id ?? stage.id
                            ]
                          : 'var(--se-rule)',
                    }}
                    aria-hidden
                  />
                )}
                <span
                  className={cn(
                    'relative z-[1] flex h-6 w-6 items-center justify-center rounded-sm border-2 text-[10px] font-bold',
                    isActive &&
                      'border-[var(--se-route-red)] bg-[var(--se-route-red)] text-white',
                    isComplete &&
                      !isActive &&
                      'border-[var(--se-route-teal)] bg-[var(--se-route-teal)] text-white',
                    isUpcoming &&
                      'border-[var(--se-rule)] bg-[var(--se-porcelain)] text-[var(--se-ink-muted)]'
                  )}
                  aria-current={isActive ? 'step' : undefined}
                >
                  {index + 1}
                </span>
                <span
                  className={cn(
                    'mt-2 text-xs font-medium',
                    isActive
                      ? 'text-[var(--se-route-red)]'
                      : 'text-[var(--se-ink-muted)]'
                  )}
                >
                  {stage.label}
                </span>
                {isActive && (
                  <span className="mt-0.5 text-[10px] uppercase tracking-wide text-[var(--se-ink-muted)]">
                    Active
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
